import { inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, map, tap } from 'rxjs';

import { NGXLogger } from 'ngx-logger';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData,
} from '../components/confirmation-dialog/confirmation-dialog.component';
import { DirtyComponent } from '../models';

const confirmDialogData: ConfirmationDialogData = {
  cancelActionText: 'NO',
  confirmActionText: 'YES',
  contents: 'CONFIRM_DISCARD_CHANGES_MSG',
  title: 'DISCARD_CHANGES',
};

/**
 * Checks if the component is dirty and confirms the discard of changes.
 *
 * @param component Dirty component.
 * @returns A boolean indicating if the route should continue.
 */
export const dirtyCheckGuard = (
  component: DirtyComponent
): boolean | Observable<boolean> => {
  const dialog = inject(MatDialog);
  const logger = inject(NGXLogger);
  logger.debug('Dirty guard check running on component', component);
  logger.debug('Component should auto save: ', component.shouldAutoSave);
  if (component.shouldAutoSave) return true; // Auto save should be taking care of saving changes so there is no need to do anything else here.
  if (component.isDirty) {
    logger.debug('Component is dirty.');
    if (typeof component.dirtyFn === 'function') {
      logger.debug('Component has a dirty function. Running it now...');
      return component.dirtyFn();
    }

    return dialog
      .open(ConfirmationDialogComponent, {
        data: confirmDialogData,
      })
      .afterClosed()
      .pipe(
        map((result) => !!result),
        tap((result) => {
          logger.debug('Dirty guard confirmation dialog result: ', result);
        })
      );
  }

  // Indicates that the route can deactivate since there are no unsaved changes.
  return true;
};
